import styled from "@emotion/styled";
import { SearchIcon } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useDrawerState } from "@/store/drawer-state";
import { useChatState } from "@/store/chat-state";
import { useLimitedInput } from "@/hooks/use-limited-input";
import type { PopularLink } from "../../constants/popular-links";
import { WIDGET_ORIGIN } from "@/constants/widget-origin";
import { useWidgetRuntimeConfig } from "@/context/widget-runtime-config";
import type { BootstrapHandle } from "@/utils/bootstrap-web-component";
import { bootstrapWidgetWebComponent } from "@/utils/bootstrap-web-component";
import { resolvePopularQuestions } from "@/utils/resolve-popular-questions";
import { DisclaimerFooter } from "../primitives/disclaimer-footer";
import { LANDING_SEARCH_PLACEHOLDER } from "@/constants/search-constants";
import { resolvePlaceholder } from "@/utils/resolve-widget-content";
import {
  backgrounds,
  borders,
  breakpoints,
  colors,
  HEADING_FONT_STACK,
  radius,
  responsiveSpacing,
  spacing,
} from "../design-tokens";

type LandingSearchBoxProps = {
  placeholder?: string;
};

const StyledForm = styled.form<{ hasError?: boolean }>`
  padding-inline: ${responsiveSpacing.safeArea.desktop}px;
  pointer-events: auto;
  width: 100%;

  margin-top: ${spacing["2xl"]}px;

  border-radius: ${radius.lg}px;
  ${props => (props.hasError ? `border: 1px solid ${colors.danger};` : borders.input)}
  ${backgrounds.glassy}
  display: flex;
  align-items: stretch;
  gap: ${spacing.md}px;
  padding: ${spacing["2xl"]}px;
  box-sizing: border-box;
  transition: border-color 200ms ease-in-out;

  @media (max-width: ${breakpoints.md}px) {
    padding: ${spacing.md}px;
    padding-top: ${spacing.lg}px;
  }
`;

const StyledTextarea = styled.textarea`
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--pollen-heading-font-family, ${HEADING_FONT_STACK});
  font-size: 26px;
  color: ${colors.text};
  flex-grow: 1;
  width: 100%;
  resize: none;

  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  font-weight: 500;
  padding: 0 8px;
  min-height: 1lh;
  height: 1lh;
  max-height: 5lh;
  overflow-y: auto;

  &::placeholder {
    color: ${colors.textPlaceholder};
    font-weight: 300;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: ${breakpoints.md}px) {
    font-size: 20px;
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  width: 28px;
  height: 28px;
  margin-block: auto;
  color: ${colors.text};
  stroke-width: 2;
`;

const PopularLinksContainer = styled.div`
  width: 100%;
  max-width: 70%;
  margin-top: ${spacing.lg}px;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.lg - 1}px) {
    max-width: 80%;
  }

  @media (max-width: ${breakpoints.md - 1}px) {
    max-width: 100%;
    padding-inline: ${spacing.md}px;
  }
`;

const PopularLinksRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
`;

const PopularLabel = styled.span`
  color: ${colors.text};
  font-weight: 500;
  font-size: 16px;
  white-space: nowrap;
`;

const PopularQuestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs}px;
  align-items: center;

  @media (max-width: ${breakpoints.md}px) {
    align-items: flex-start;
    flex-direction: column;
    gap: ${spacing.sm}px;
  }
`;

const PopularQuestion = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs}px;
`;

const StyledPopularLink = styled.button`
  color: ${colors.text};
  text-decoration: underline;
  font-size: 16px;
  transition: color 200ms ease-in-out;
  outline: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;

  &:hover {
    color: ${colors.primary};
  }

  &:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const LinkSeparator = styled.span`
  color: ${colors.text};
  font-size: 16px;
  user-select: none;

  @media (max-width: ${breakpoints.md}px) {
    display: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: 14px;
  margin-top: ${spacing.sm}px;
  padding-left: 44px; // Align with textarea content (search icon width + gap)
  max-width: min(650px, 100vw - 32px); /* Prevent overflow on small screens */
  width: 100%;
  transition: opacity 200ms ease-in-out;
`;

const buildPopularQuestionKey = (
  link: PopularLink,
  index: number,
  links: PopularLink[],
) => {
  const keyBase = `${link.displayText}:${link.query ?? link.displayText}`;
  const duplicateCount = links
    .slice(0, index)
    .filter(previousLink =>
      `${previousLink.displayText}:${previousLink.query ?? previousLink.displayText}` === keyBase)
    .length;

  return duplicateCount > 0 ? `${keyBase}:${duplicateCount}` : keyBase;
};

const LandingSearchBoxComponent: React.FC<LandingSearchBoxProps> = ({
  placeholder = LANDING_SEARCH_PLACEHOLDER,
}) => {
  const limitedInput = useLimitedInput("", { showError: true });
  const { openDrawer } = useDrawerState();
  const { submitQuery } = useChatState();
  const { widgetSuggestedQuestions, showLandingQuestions } = useWidgetRuntimeConfig();
  const resolvedQuestions = resolvePopularQuestions(widgetSuggestedQuestions);
  const popularQuestions = showLandingQuestions ? resolvedQuestions : [];
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [limitedInput.value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (limitedInput.value.trim() && limitedInput.isValid) {
      submitQuery(limitedInput.value.trim(), WIDGET_ORIGIN.LANDING_SEARCH_BOX);
      openDrawer();
      limitedInput.reset();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    limitedInput.handleChange(e);
    setTimeout(adjustTextareaHeight, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (limitedInput.isValid) {
        handleSubmit(e as any);
      }
    }
  };

  return (
    <>
      <StyledForm
        onSubmit={handleSubmit}
        role="search"
        aria-label="AI assistant search"
        hasError={!!limitedInput.error}
      >
        <StyledSearchIcon aria-hidden="true" role="img" aria-label="Search icon" />
        <StyledTextarea
          ref={textareaRef}
          name="query"
          value={limitedInput.value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          aria-label="Ask AI assistant a question"
          aria-description="Press Enter to submit, Shift+Enter for new line"
          aria-keyshortcuts="Enter Shift+Enter"
          aria-invalid={!!limitedInput.error}
          aria-errormessage={limitedInput.error ? "query-error" : undefined}
          autoComplete="off"
          spellCheck="true"
          role="textbox"
          aria-multiline="true"
          aria-expanded={limitedInput.value.includes("\n")}
          title="Press Enter to submit, Shift+Enter for new line"
        />
      </StyledForm>
      {limitedInput.error && (
        <ErrorMessage id="query-error" role="alert">
          {limitedInput.error}
        </ErrorMessage>
      )}

      <DisclaimerFooter layout="landing" />

      {popularQuestions.length > 0 && (
        <PopularLinksContainer>
          <PopularLinksRow>
            <PopularLabel>Popular:</PopularLabel>
            <PopularQuestions>
              {popularQuestions.map((link, index) => {
                return (
                  <PopularQuestion key={buildPopularQuestionKey(link, index, popularQuestions)}>
                    <StyledPopularLink
                      onClick={() => {
                        submitQuery(link.query ?? link.displayText, WIDGET_ORIGIN.LANDING_SEARCH_BOX);
                        openDrawer();
                      }}
                      aria-label={`Ask about: ${link.displayText}`}
                      type="button"
                    >
                      {link.displayText}
                    </StyledPopularLink>
                    {index < popularQuestions.length - 1 && <LinkSeparator>|</LinkSeparator>}
                  </PopularQuestion>
                );
              })}
            </PopularQuestions>
          </PopularLinksRow>
        </PopularLinksContainer>
      )}
    </>
  );
};

class LandingSearchBoxWebComponent extends HTMLElement {
  private handle: BootstrapHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const placeholder = this.getAttribute("placeholder") ?? undefined;

    this.handle = bootstrapWidgetWebComponent(this, {
      componentName: "landing-search-box",
      render: config => (
        <LandingSearchBoxComponent
          placeholder={resolvePlaceholder(
            placeholder,
            config.placeholderText,
            LANDING_SEARCH_PLACEHOLDER,
          )}
        />
      ),
    });
  }

  disconnectedCallback() {
    this.handle?.unmount();
    this.handle = null;
  }
}

export default LandingSearchBoxWebComponent;
